
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit2, Trash2, Calendar, User, GripVertical } from "lucide-react";
import { ProjectSubtask } from '../types';

interface DraggableSubtaskProps {
  subtask: ProjectSubtask;
  onEdit: (subtask: ProjectSubtask) => void;
  onDelete: (subtaskId: string) => void;
  onStatusChange: (subtaskId: string, status: 'To Do' | 'In Progress' | 'Done') => void;
  isRestrictedUser?: boolean;
}

const DraggableSubtask = ({ subtask, onEdit, onDelete, onStatusChange, isRestrictedUser = false }: DraggableSubtaskProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: subtask.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  const getCardGradient = (status: string) => {
    switch (status) {
      case 'Done':
        return 'bg-gradient-to-br from-green-500/20 via-green-400/10 to-emerald-500/20';
      case 'In Progress':
        return 'bg-gradient-to-br from-yellow-500/20 via-orange-400/10 to-amber-500/20';
      default:
        return 'bg-gradient-to-br from-gray-500/20 via-slate-400/10 to-gray-600/20';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Done':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'In Progress':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      default:
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const statusOrder = ['To Do', 'In Progress', 'Done'];
    const currentIndex = statusOrder.indexOf(subtask.status);
    const nextStatus = statusOrder[(currentIndex + 1) % statusOrder.length] as 'To Do' | 'In Progress' | 'Done';
    onStatusChange(subtask.id, nextStatus);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
    >
      <Card className={`${getCardGradient(subtask.status)} border border-primary/20 backdrop-blur-sm hover:border-primary/40 transition-all duration-200 shadow-lg hover:shadow-xl`}>
        <CardContent className="p-3">
          {/* Drag Handle and Header */}
          <div className="flex items-center justify-between mb-2">
            <Badge 
              variant="outline" 
              className={`text-xs px-2 py-1 border cursor-pointer ${getStatusColor(subtask.status)}`}
              onClick={handleStatusClick}
            >
              {subtask.status}
            </Badge>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(subtask);
                }}
                disabled={isRestrictedUser}
                className={`h-6 w-6 p-0 ${
                  isRestrictedUser 
                    ? "text-gray-500 cursor-not-allowed hover:text-gray-500 hover:bg-transparent"
                    : "text-blue-300 hover:text-blue-200 hover:bg-blue-500/20"
                }`}
              >
                <Edit2 className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(subtask.id);
                }}
                disabled={isRestrictedUser}
                className={`h-6 w-6 p-0 ${
                  isRestrictedUser 
                    ? "text-gray-500 cursor-not-allowed hover:text-gray-500 hover:bg-transparent"
                    : "text-red-300 hover:text-red-200 hover:bg-red-500/20"
                }`}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
              <div
                {...(!isRestrictedUser ? attributes : {})}
                {...(!isRestrictedUser ? listeners : {})}
                className={`p-1 ${
                  isRestrictedUser 
                    ? "text-gray-500 cursor-not-allowed"
                    : "cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-300"
                }`}
              >
                <GripVertical className="h-3 w-3" />
              </div>
            </div>
          </div>

          {/* Subtask Name - also draggable */}
          <h4 
            className={`text-white font-medium text-sm mb-2 line-clamp-2 ${
              isRestrictedUser 
                ? "cursor-default"
                : "cursor-grab active:cursor-grabbing"
            }`}
            {...(!isRestrictedUser ? attributes : {})}
            {...(!isRestrictedUser ? listeners : {})}
          >
            {subtask.subtask_name}
          </h4>

          {/* Assigned Resource */}
          <div className="flex items-center gap-1 mb-2 text-xs text-gray-300">
            <User className="h-3 w-3" />
            <span className="truncate">{subtask.name || 'Unassigned'}</span>
          </div>

          {/* Dates */}
          <div className="flex items-center justify-between text-xs text-gray-400">
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{formatDate(subtask.start_date)}</span>
            </div>
            <div className="flex items-center gap-1">
              <span>→</span>
              <span>{formatDate(subtask.end_date)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DraggableSubtask;
